/**
 * PropertyPicker — reine Multi-Select-Checkbox-Liste (Vorbild: MoveFileDialog).
 * Callback-basiert, keine Server-Interaktion → gut unit-testbar. Optionaler
 * Client-Filter für lange Property-Listen.
 */
import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { Input } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import type { AssignableProperty } from './useAssignableProperties';

interface PropertyPickerProps {
  properties: AssignableProperty[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  disabledIds?: string[];
  emptyHint?: string;
}

export function PropertyPicker({
  properties,
  selectedIds,
  onToggle,
  disabledIds = [],
  emptyHint = 'Keine Apartments vorhanden.',
}: PropertyPickerProps) {
  const [filter, setFilter] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const disabled = useMemo(() => new Set(disabledIds), [disabledIds]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter((p) => p.name.toLowerCase().includes(q));
  }, [properties, filter]);

  if (properties.length === 0) {
    return <p className="text-ink-faint py-4 text-center text-sm">{emptyHint}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {properties.length > 6 && (
        <Input
          placeholder="Apartment suchen…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      )}
      <ul className="border-line/50 divide-line/40 max-h-56 divide-y overflow-y-auto rounded-md border">
        {visible.map((p) => {
          const isSelected = selected.has(p.id);
          const isDisabled = disabled.has(p.id);
          return (
            <li key={p.id}>
              <button
                type="button"
                role="checkbox"
                aria-checked={isSelected}
                aria-label={p.name}
                disabled={isDisabled}
                onClick={() => onToggle(p.id)}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors',
                  isDisabled ? 'text-ink-faint cursor-not-allowed' : 'text-ink hover:bg-card-alt',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 flex-none items-center justify-center rounded border',
                    isSelected ? 'bg-navy border-navy text-white' : 'border-line bg-white',
                  )}
                >
                  {isSelected && <Check size={12} strokeWidth={3} />}
                </span>
                <span className="truncate">{p.name}</span>
              </button>
            </li>
          );
        })}
        {visible.length === 0 && (
          <li className="text-ink-faint px-3 py-4 text-center text-sm">Kein Treffer.</li>
        )}
      </ul>
    </div>
  );
}
